import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { tbqFunctions } from './firebase-config';
import { httpsCallable } from 'firebase/functions';
import DTQLocationForm from './DTQLocationForm';
import LocationHistoryModal from './LocationHistoryModal';

const ENABLE_LOCATION_HISTORY_UI = process.env.REACT_APP_ENABLE_LOCATION_HISTORY_UI !== 'false';

const FilterComponent = ({
  filterText,
  onFilter,
  cityFilter,
  onCityFilter,
  typeFilter,
  onTypeFilter,
  statusFilter,
  onStatusFilter,
  cities,
}) => (
  <div className="filters-row">
    <input
      id="search"
      type="text"
      placeholder="Search by Name, City, Address, Mudarris, Contact, or Ladies Message"
      aria-label="Search Input"
      value={filterText}
      onChange={onFilter}
      className="filter-input"
    />
    <select value={cityFilter} onChange={onCityFilter} className="filter-select">
      <option value="">All Cities</option>
      {cities.map((city) => (
        <option key={city} value={city}>{city}</option>
      ))}
    </select>
    <select value={typeFilter} onChange={onTypeFilter} className="filter-select">
      <option value="">All Types</option>
      <option value="0">DTQ (Regular)</option>
      <option value="1">Summary (خلاصہ)</option>
    </select>
    <select value={statusFilter} onChange={onStatusFilter} className="filter-select">
      <option value="active">Active</option>
      <option value="deleted">Deleted</option>
      <option value="all">All</option>
    </select>
  </div>
);

const convertToCSV = (objArray, columns) => {
  const headers = columns
    .filter((col) => col.name !== 'Actions')
    .map((column) => column.name)
    .join(',') + '\r\n';
  const rows = objArray.map((obj) => {
    return columns
      .filter((col) => col.name !== 'Actions')
      .map((column) => {
        const value = typeof column.selector === 'function' ? column.selector(obj) : '';
        const stringValue = value ? value.toString().replace(/"/g, '""') : '';
        return `"${stringValue}"`;
      }).join(',');
  }).join('\r\n');
  return headers + rows;
};

const downloadCSV = (csv, filename) => {
  const csvFile = new Blob([csv], { type: 'text/csv' });
  const downloadLink = document.createElement('a');
  downloadLink.download = filename;
  downloadLink.href = window.URL.createObjectURL(csvFile);
  downloadLink.style.display = 'none';
  document.body.appendChild(downloadLink);
  downloadLink.click();
};

const DTQLocations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isExporting, setIsExporting] = useState(false);
  const [existingIds, setExistingIds] = useState([]);
  const [existingCities, setExistingCities] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [historyLocation, setHistoryLocation] = useState(null);
  const [opError, setOpError] = useState('');
  const mountedRef = useRef(false);

  const softDeleteDtqLocation = httpsCallable(tbqFunctions, 'softDeleteDtqLocation');
  const restoreDtqLocation = httpsCallable(tbqFunctions, 'restoreDtqLocation');
  const listDtqLocations = useMemo(() => httpsCallable(tbqFunctions, 'listDtqLocations'), []);
  const listDtqLocationMeta = useMemo(() => httpsCallable(tbqFunctions, 'listDtqLocationMeta'), []);

  const refreshMeta = useCallback(async () => {
    try {
      const response = await listDtqLocationMeta({});
      const data = response.data || {};
      setExistingIds(Array.isArray(data.ids) ? data.ids : []);
      setExistingCities(Array.isArray(data.cities) ? data.cities : []);
    } catch (error) {
      console.error('Failed to load location metadata:', error);
    }
  }, [listDtqLocationMeta]);

  const loadLocationsPage = useCallback(async (page, pageSize, searchText = debouncedSearch) => {
    setLoading(true);
    try {
      const response = await listDtqLocations({
        statusFilter,
        cityFilter,
        typeFilter,
        search: searchText || '',
        page,
        pageSize,
        sortField,
        sortDirection,
      });
      const data = response.data || {};
      setLocations(Array.isArray(data.rows) ? data.rows : []);
      setTotalRows(Number(data.totalRows || 0));
    } catch (error) {
      console.error('Error fetching locations:', error);
      setOpError(`Failed to load locations: ${error.message}`);
      setLocations([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [listDtqLocations, statusFilter, cityFilter, typeFilter, sortField, sortDirection, debouncedSearch]);

  useEffect(() => {
    refreshMeta();
  }, [refreshMeta]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filterText), 300);
    return () => clearTimeout(timer);
  }, [filterText]);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      loadLocationsPage(1, rowsPerPage, debouncedSearch);
      return;
    }
    setCurrentPage(1);
    loadLocationsPage(1, rowsPerPage, debouncedSearch);
  }, [statusFilter, cityFilter, typeFilter, sortField, sortDirection, debouncedSearch, rowsPerPage, loadLocationsPage]);

  const refreshLocations = useCallback(async () => {
    setCurrentPage(1);
    await Promise.all([
      refreshMeta(),
      loadLocationsPage(1, rowsPerPage, debouncedSearch),
    ]);
  }, [refreshMeta, loadLocationsPage, rowsPerPage, debouncedSearch]);

  const handleSoftDelete = useCallback(async (id) => {
    try {
      setOpError('');
      await softDeleteDtqLocation({ id });
      setDeleteConfirm(null);
      refreshLocations();
    } catch (error) {
      console.error('Error soft deleting location:', error);
      setOpError(`Failed to delete: ${error.message}`);
    }
  }, [softDeleteDtqLocation, refreshLocations]);

  const handleRestore = useCallback(async (id) => {
    try {
      setOpError('');
      await restoreDtqLocation({ id });
      refreshLocations();
    } catch (error) {
      console.error('Error restoring location:', error);
      setOpError(`Failed to restore: ${error.message}`);
    }
  }, [restoreDtqLocation, refreshLocations]);

  const handleEdit = useCallback((location) => {
    setOpError('');
    setEditingLocation(location);
    setShowForm(true);
  }, []);

  const handleAdd = () => {
    setOpError('');
    setEditingLocation(null);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingLocation(null);
  };

  const handleFormSave = () => {
    setShowForm(false);
    setEditingLocation(null);
    refreshLocations();
  };

  const cities = existingCities;

  const columns = useMemo(() => [
    {
      name: 'ID',
      selector: (row) => row.id,
      sortable: true,
      width: '80px',
      sortKey: 'id',
    },
    {
      name: 'Name (Urdu)',
      selector: (row) => row.name,
      sortable: true,
      wrap: true,
      minWidth: '180px',
      sortKey: 'name',
      cell: (row) => <div style={{ direction: 'rtl', textAlign: 'right', width: '100%' }}>{row.name}</div>,
    },
    {
      name: 'English Name',
      selector: (row) => row.english,
      sortable: true,
      wrap: true,
      minWidth: '140px',
      sortKey: 'english',
    },
    {
      name: 'City',
      selector: (row) => row.city,
      sortable: true,
      minWidth: '140px',
      sortKey: 'city',
    },
    {
      name: 'Address',
      selector: (row) => row.address,
      sortable: true,
      wrap: true,
      minWidth: '180px',
      sortKey: 'address',
      cell: (row) => <div style={{ direction: 'rtl', textAlign: 'right', width: '100%' }}>{row.address}</div>,
    },
    {
      name: 'Mudarris',
      selector: (row) => row.mudarris,
      sortable: true,
      wrap: true,
      minWidth: '150px',
      sortKey: 'mudarris',
      cell: (row) => <div style={{ direction: 'rtl', textAlign: 'right', width: '100%' }}>{row.mudarris}</div>,
    },
    {
      name: 'Contact',
      selector: (row) => row.contact,
      sortable: true,
      minWidth: '130px',
      sortKey: 'contact',
    },
    {
      name: 'Time',
      selector: (row) => row.time,
      sortable: true,
      wrap: true,
      minWidth: '150px',
      sortKey: 'time',
      cell: (row) => <div style={{ direction: 'rtl', textAlign: 'right', width: '100%' }}>{row.time}</div>,
    },
    {
      name: 'Type',
      selector: (row) => (row.type === 0 ? 'DTQ' : 'Summary'),
      sortable: true,
      width: '90px',
      sortKey: 'type',
      cell: (row) => (
        <span className={`type-badge ${row.type === 0 ? 'type-dtq' : 'type-summary'}`}>
          {row.type === 0 ? 'DTQ' : 'Summary'}
        </span>
      ),
    },
    {
      name: 'Status',
      selector: (row) => (row.is_deleted ? 'Deleted' : 'Active'),
      sortable: true,
      width: '95px',
      sortKey: 'status',
      cell: (row) => (
        <span className={`status-badge ${row.is_deleted ? 'status-deleted' : 'status-active'}`}>
          {row.is_deleted ? 'Deleted' : 'Active'}
        </span>
      ),
    },
    {
      name: 'Ladies',
      selector: (row) => (row.isLadies ? 'Yes' : 'No'),
      sortable: true,
      width: '80px',
      sortKey: 'ladies',
      cell: (row) => (
        <span className={`ladies-badge ${row.isLadies ? 'ladies-yes' : 'ladies-no'}`}>
          {row.isLadies ? '✓' : '✗'}
        </span>
      ),
    },
    {
      name: 'Updated',
      selector: (row) => {
        if (!row.updated_at_ms) return '';
        return new Date(row.updated_at_ms).toISOString();
      },
      sortable: true,
      width: '160px',
      sortKey: 'updated',
      cell: (row) => (
        row.updated_at_ms
          ? new Date(row.updated_at_ms).toLocaleString()
          : <span style={{ color: '#999' }}>—</span>
      ),
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div className="action-buttons">
          {!row.is_deleted && (
            <button className="btn-edit" onClick={() => handleEdit(row)} title="Edit">
              ✎
            </button>
          )}
          {ENABLE_LOCATION_HISTORY_UI && (
            <button className="btn-history" onClick={() => setHistoryLocation(row)} title="History">
              ⟳
            </button>
          )}
          {row.is_deleted ? (
            <button className="btn-restore" onClick={() => handleRestore(row.id)} title="Restore">
              ↺
            </button>
          ) : (
            <button className="btn-delete" onClick={() => setDeleteConfirm(row.id)} title="Delete">
              ✕
            </button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      width: '130px',
    },
  ], [handleEdit, handleRestore, setHistoryLocation, setDeleteConfirm]);

  const filteredItems = locations;

  const handlePageChange = async (page) => {
    setCurrentPage(page);
    await loadLocationsPage(page, rowsPerPage, debouncedSearch);
  };

  const handleRowsPerPageChange = async (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
    await loadLocationsPage(1, newRowsPerPage, debouncedSearch);
  };

  const handleSort = (column, direction) => {
    const nextSortField = column.sortKey || 'id';
    setSortField(nextSortField);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const exportLocations = useCallback(async () => {
    setIsExporting(true);
    try {
      const allRows = [];
      let page = 1;
      let hasNext = true;

      while (hasNext) {
        const response = await listDtqLocations({
          statusFilter,
          cityFilter,
          typeFilter,
          search: debouncedSearch || '',
          page,
          pageSize: 200,
          sortField,
          sortDirection,
        });
        const data = response.data || {};
        const rows = Array.isArray(data.rows) ? data.rows : [];
        allRows.push(...rows);
        hasNext = Boolean(data.hasMore);
        page += 1;
      }

      const csv = convertToCSV(allRows, columns);
      downloadCSV(csv, 'dtq-locations.csv');
    } catch (error) {
      console.error('Failed to export locations:', error);
      setOpError(`Export failed: ${error.message}`);
    } finally {
      setIsExporting(false);
    }
  }, [listDtqLocations, statusFilter, cityFilter, typeFilter, debouncedSearch, sortField, sortDirection, columns]);

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <div className="table-header">
        <FilterComponent
          filterText={filterText}
          onFilter={(e) => setFilterText(e.target.value)}
          cityFilter={cityFilter}
          onCityFilter={(e) => setCityFilter(e.target.value)}
          typeFilter={typeFilter}
          onTypeFilter={(e) => setTypeFilter(e.target.value)}
          statusFilter={statusFilter}
          onStatusFilter={(e) => setStatusFilter(e.target.value)}
          cities={cities}
        />
        <div className="table-actions">
          <button className="btn-add" onClick={handleAdd}>
            + Add Location
          </button>
          <button className="btn-export" onClick={exportLocations} disabled={isExporting}>
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </button>
          <button className="btn-refresh" onClick={refreshLocations}>
            ↻ Refresh
          </button>
        </div>
      </div>
    );
  }, [filterText, cityFilter, typeFilter, statusFilter, cities, isExporting, exportLocations, refreshLocations]);

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: '#183D24',
        color: '#ffffff',
        fontWeight: '600',
        fontSize: '13px',
      },
    },
    cells: {
      style: {
        fontSize: '13px',
        padding: '8px 12px',
      },
    },
    rows: {
      highlightOnHoverStyle: {
        backgroundColor: '#e6f0ea',
      },
    },
  };

  return (
    <div className="dtq-locations-container">
      {opError ? <div className="form-error">{opError}</div> : null}
      <DataTable
        title={(
          <div className="table-title">
            <span>DTQ Locations</span>
            <span className="location-count">{filteredItems.length} of {totalRows} locations</span>
          </div>
        )}
        columns={columns}
        data={filteredItems}
        subHeader
        subHeaderComponent={subHeaderComponentMemo}
        persistTableHead
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationDefaultPage={currentPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handleRowsPerPageChange}
        paginationPerPage={rowsPerPage}
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
        sortServer
        onSort={handleSort}
        highlightOnHover
        responsive
        progressPending={loading}
        progressComponent={<div className="loading-spinner">Loading locations...</div>}
        customStyles={customStyles}
        dense
      />

      {showForm && (
        <DTQLocationForm
          location={editingLocation}
          onClose={handleFormClose}
          onSave={handleFormSave}
          existingIds={existingIds}
          existingCities={cities}
        />
      )}

      {ENABLE_LOCATION_HISTORY_UI && historyLocation && (
        <LocationHistoryModal
          location={historyLocation}
          onClose={() => setHistoryLocation(null)}
        />
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Soft Delete</h3>
            <p>Are you sure you want to archive location <strong>{deleteConfirm}</strong>?</p>
            <p className="delete-warning">You can restore this location later from deleted view.</p>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn-confirm-delete" onClick={() => handleSoftDelete(deleteConfirm)}>Archive</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DTQLocations;
